// MfaChallengePage — v2 MFA verification page (CHG-1510 / ITER-3628).
//
// η.1 of the MFA program (/tmp/mfa-program.md). Mounts at
// /mfa-challenge. Reached when LoginPage gets a LoginResult of
// kind "mfa_required" — it passes mfa_token + mfa_methods + returnTo
// via location.state.
//
// Token persistence across page refresh: we mirror mfa_token into
// sessionStorage on mount so a user who reloads /mfa-challenge mid-
// flow doesn't get stranded (location.state vanishes on reload).
// The mirror is cleared on a successful verify or a navigate-away
// to /login.
//
// Two paths via "Use recovery code instead" toggle:
//   - TOTP        → AuthContext.verifyMfa(mfa_token, code)
//   - Recovery    → AuthContext.verifyMfaRecovery(mfa_token, code)
//
// On success both paths land in AuthContext.completeAuthenticatedSession
// (token store + /me hydrate) and return { kind: 'ok' }. The page
// then navigates to returnTo (default "/" — already open-redirect-
// clamped by LoginPage's safeReturnTo when the value was put into
// state).
//
// No real OtpField primitive exists yet (per field-primitive registry
// stub note); per /tmp/mfa-program.md doctrine the page uses a
// minimal numeric input with autoComplete="one-time-code" and
// inputMode="numeric". A real OtpField is the queued follow-up.

import { useEffect, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { AuthLayout } from "./AuthLayout";
import { inputStyle, labelStyle, errorStyle, submitStyle } from "./styles";

const MFA_TOKEN_STORAGE_KEY = "mfa_token_pending";
const TOTP_CODE_LENGTH = 6;

type State = "form" | "submitting" | "error";

interface MfaChallengeNavState {
  mfa_token?: string;
  mfa_methods?: string[];
  returnTo?: string;
}

function safeReturnTo(raw: string | null | undefined): string {
  if (!raw) return "/";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/";
  return raw;
}

function readMfaTokenFromStorage(): string {
  try {
    return window.sessionStorage.getItem(MFA_TOKEN_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function writeMfaTokenToStorage(token: string): void {
  try {
    window.sessionStorage.setItem(MFA_TOKEN_STORAGE_KEY, token);
  } catch {
    // best-effort
  }
}

function clearMfaTokenStorage(): void {
  try {
    window.sessionStorage.removeItem(MFA_TOKEN_STORAGE_KEY);
  } catch {
    // best-effort
  }
}

export default function MfaChallengePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyMfa, verifyMfaRecovery } = useAuth();

  const navState = (location.state ?? {}) as MfaChallengeNavState;
  // Prefer location.state on first render; fall back to sessionStorage
  // mirror on reload.
  const [mfaToken] = useState<string>(
    () => navState.mfa_token || readMfaTokenFromStorage(),
  );
  const returnTo = safeReturnTo(navState.returnTo);

  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState("");
  const [state, setState] = useState<State>("form");
  const [error, setError] = useState<string>("");

  // Mirror the token into sessionStorage on mount so a refresh during
  // the challenge doesn't strand the user.
  useEffect(() => {
    if (navState.mfa_token) writeMfaTokenToStorage(navState.mfa_token);
  }, [navState.mfa_token]);

  // No token + no mirror → user navigated here directly. Bounce them
  // back to /login so they can re-enter credentials.
  useEffect(() => {
    if (!mfaToken) {
      navigate("/login", { replace: true });
    }
  }, [mfaToken, navigate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (!code.trim()) {
      setError(
        useRecovery
          ? "Enter one of your recovery codes."
          : "Enter the 6-digit code from your authenticator app.",
      );
      setState("error");
      return;
    }
    if (!useRecovery && code.replace(/\s+/g, "").length !== TOTP_CODE_LENGTH) {
      setError("The authenticator code is a 6-digit number.");
      setState("error");
      return;
    }
    setState("submitting");
    try {
      const result = useRecovery
        ? await verifyMfaRecovery(mfaToken, code.trim())
        : await verifyMfa(mfaToken, code.replace(/\s+/g, ""));
      if (result.kind === "ok") {
        clearMfaTokenStorage();
        navigate(returnTo, { replace: true });
        return;
      }
      // Backend re-issued mfa_required (rate-limit edge or wrong
      // code that didn't 401 — the verify endpoint usually surfaces
      // wrong-code as a 400/403 caught below). Treat as a soft
      // retry; reset the code field.
      if (result.kind === "mfa_required") {
        setCode("");
        setError("That code didn't work. Try again.");
        setState("error");
        return;
      }
      // mfa_setup_required from a verify endpoint is unexpected —
      // surface a clear next-step rather than silently no-op.
      setError(
        "Multi-factor enrollment required. Sign in via " +
          "app.aloragiving.com to complete setup.",
      );
      setState("error");
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(
        detail ??
          (useRecovery
            ? "Recovery code not accepted. Try another, or use your authenticator app."
            : "Code not accepted. Check your authenticator app and try again."),
      );
      setState("error");
    }
  }

  const submitting = state === "submitting";

  return (
    <AuthLayout
      pageId="mfa-challenge"
      title={useRecovery ? "Enter a recovery code" : "Two-factor verification"}
      subtitle={
        useRecovery
          ? "Use one of the one-time recovery codes you saved when you enabled 2FA."
          : "Enter the 6-digit code from your authenticator app."
      }
    >
      <form onSubmit={onSubmit} data-form="mfa-challenge">
        <label style={labelStyle}>
          {useRecovery ? "Recovery code" : "Authenticator code"}
          {useRecovery ? (
            <input
              type="text"
              autoComplete="one-time-code"
              autoFocus
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={submitting}
              data-field="recovery-code"
              style={inputStyle}
            />
          ) : (
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              autoFocus
              required
              maxLength={8} // tolerate spaces/dashes; we strip them on submit
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={submitting}
              data-field="totp-code"
              style={inputStyle}
            />
          )}
        </label>

        {error && (
          <div data-role="error" style={errorStyle}>
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-action="submit"
          style={submitStyle(submitting)}
        >
          {submitting
            ? "Verifying…"
            : useRecovery
            ? "Use recovery code"
            : "Verify"}
        </button>

        {/* CHG-2954 (UM program Slice G) — escape hatch for the expired
            mfa_token state (purpose=mfa-challenge JWT, 5-min TTL). The
            verify endpoints 401 BOTH on a wrong code and on an expired
            token; once the token expires every retry fails, so the page
            needs a way back to /login to mint a fresh challenge (v1's
            "Back to login" affordance — documented state expired_token in
            specs/tier-05-experience/mfa-sso-flows.cross-screen.yaml). */}
        <button
          type="button"
          data-action="back-to-login"
          onClick={() => {
            clearMfaTokenStorage();
            navigate("/login", { replace: true });
          }}
          disabled={submitting}
          style={{
            marginTop: 12,
            background: "none",
            border: "none",
            padding: 0,
            fontSize: 13,
            color: "var(--color-text-muted)",
            cursor: submitting ? "not-allowed" : "pointer",
            textAlign: "center",
            width: "100%",
          }}
        >
          Back to sign-in
        </button>

        <button
          type="button"
          data-action="toggle-recovery"
          onClick={() => {
            setUseRecovery((prev) => !prev);
            setCode("");
            setError("");
            setState("form");
          }}
          disabled={submitting}
          style={{
            marginTop: 12,
            background: "none",
            border: "none",
            padding: 0,
            fontSize: 13,
            color: "var(--color-primary)",
            cursor: submitting ? "not-allowed" : "pointer",
            textAlign: "center",
            width: "100%",
          }}
        >
          {useRecovery
            ? "Use your authenticator app instead"
            : "Use a recovery code instead"}
        </button>
      </form>
    </AuthLayout>
  );
}
